import { useCallback, useEffect, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useChainManager } from './useChainManager';
import { useMintLixiEvent } from './useBlockchainEvents';
import {
  CreatingStage,
  selectCurrentCreatingStage,
  selectCurrentGroupId,
  selectCurrentBlockhashHash,
  selectCurrentCreatorWalletAddress,
  selectCurrentLixiBagAddress,
  selectEnvelopeFormFields,
  setCreatingStage,
  setGroupId,
} from '@features';
import { useAppDispatch, useAppSelector } from '@hooks';
import { useCreateCampaignMutation, useUploadMutation } from '@app/services';
import { calculateBonusNft, dataURLtoFile, prepareUploadbody } from '@utils';
import { TEMPLATE_ID } from '@constants';
import { useNavigate } from 'react-router';
import { Network, mapChainToNetwork } from '../../types/network.enum';

/**
 * Unified hook for MintLixi event handling across all chains
 * Replaces the old useMintLixiListener that only worked with EVM chains
 *
 * Handles the complete flow:
 * 1. Listen to MintLixi events
 * 2. Upload logo
 * 3. Create campaign via API
 */
export function useMintLixi() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const fieldState = useAppSelector(selectEnvelopeFormFields());
  const creatingStage = useAppSelector(selectCurrentCreatingStage);
  const lixiBagAddress = useAppSelector(selectCurrentLixiBagAddress);
  const blockhashHash = useAppSelector(selectCurrentBlockhashHash);
  const creatorWalletAddress = useAppSelector(selectCurrentCreatorWalletAddress);
  const { wallet, currentChain } = useChainManager();
  const [upload] = useUploadMutation();
  const [createCampaign] = useCreateCampaignMutation();
  const [isMintLixiFired, setIsMintLixiFired] = useState(false);
  const [solanaTxProcessed, setSolanaTxProcessed] = useState(false);

  const toast = useToast({
    duration: 2000,
    isClosable: true,
    position: 'bottom',
  });

  // Handler for MintLixi event
  const handleMintLixi = useCallback(
    async (data: {
      owner: string;
      groupId: string;
      numTickets: number;
      startTicketId: string;
      purchaseDate?: number;
      globalJackpotAdded?: string;
    }) => {
      console.log("Event 'MintLixi' with payload");
      console.log(data);

      // Prevent duplicate processing
      if (isMintLixiFired) return;

      // Verify this is for the current wallet
      if (
        !wallet?.address ||
        data.owner.toLowerCase() !== wallet.address.toLowerCase()
      ) {
        return;
      }

      setIsMintLixiFired(true);

      // Parse groupId safely - handle large numbers using BigInt
      // Events return string, we support both number and BigInt
      let groupIdNum: number | string;
      try {
        // Try to parse as BigInt first to handle large numbers
        const groupIdBigInt = BigInt(data.groupId);
        const groupIdNumber = Number(data.groupId);
        
        // Check if it's within safe integer range
        if (groupIdNumber > Number.MAX_SAFE_INTEGER || groupIdNumber < Number.MIN_SAFE_INTEGER || isNaN(groupIdNumber)) {
          // Use BigInt for large numbers - store as string to avoid precision loss
          groupIdNum = groupIdBigInt.toString();
          console.log('[useMintLixi] Using BigInt for large group_id:', groupIdNum);
        } else {
          // Use number for safe integer range
          groupIdNum = groupIdNumber;
        }
        
        // Save groupId to Redux store (as number if safe, otherwise we'll handle it differently)
        // For now, try to save as number if possible, otherwise we'll need to update Redux type
        if (typeof groupIdNum === 'string') {
          // For large numbers, we can't store in Redux as number, so we'll skip storing
          // The groupId will be passed directly to API
          console.log('[useMintLixi] Large group_id detected, skipping Redux store (will use directly)');
        } else {
          dispatch(setGroupId(groupIdNum));
          console.log('[useMintLixi] Saved groupId to Redux store:', groupIdNum);
        }
      } catch (error) {
        console.error('[useMintLixi] Failed to parse groupId:', error);
        throw error;
      }
      
      // Parse startTicketId safely - handle large numbers using BigInt
      let startTicketIdNum: number;
      try {
        const startTicketIdBigInt = BigInt(data.startTicketId);
        const startTicketIdNumber = Number(data.startTicketId);
        
        // Check if it's within safe integer range
        if (startTicketIdNumber > Number.MAX_SAFE_INTEGER || startTicketIdNumber < Number.MIN_SAFE_INTEGER || isNaN(startTicketIdNumber)) {
          // For large numbers, we need to use BigInt for calculations
          // But since calculateBonusNft and numberOfItem are numbers, we'll need to handle carefully
          console.warn('[useMintLixi] Large startTicketId detected:', data.startTicketId);
          // Try to convert to number anyway for calculations (may lose precision)
          startTicketIdNum = Number(startTicketIdBigInt);
        } else {
          startTicketIdNum = startTicketIdNumber;
        }
      } catch (error) {
        console.error('[useMintLixi] Failed to parse startTicketId:', error);
        throw new Error(`Invalid startTicketId format: ${data.startTicketId}`);
      }
      const {
        otherTokenAddress,
        logoUri,
        logoName,
        endTime: endTimeStr,
        ...restFields
      } = fieldState;

      try {
        dispatch(setCreatingStage(CreatingStage.UPLOADING_LOGO));
        const file = dataURLtoFile(logoUri, logoName);
        if (!file) throw new Error('Upload file is null');

        const formData = prepareUploadbody(file);
        const uploadPayload = await upload({
          folder: 'logo',
          body: formData,
        }).unwrap();

        dispatch(setCreatingStage(CreatingStage.CREATING_ON_BE));

        const endTime = new Date(endTimeStr);
        const numberOfItem = restFields['numberItem'];

        // Convert groupId to number or string for API
        // API should accept both, but we'll send as number if safe, otherwise as string
        const groupIdForApi = typeof groupIdNum === 'string' 
          ? groupIdNum 
          : groupIdNum;
        
        // Map currentChain sang Network enum
        // Mặc định là POLYGON nếu không map được
        const network = mapChainToNetwork(currentChain || 'bnb');

        const createPayload = {
          ...restFields,
          endTime,
          logo: uploadPayload.Location,
          templateId: TEMPLATE_ID,
          templatePosition: 'CENTER',
          groupId: groupIdForApi,
          startTokenId: startTicketIdNum - calculateBonusNft(numberOfItem),
          endTokenId: startTicketIdNum + numberOfItem,
          network, // Thêm network vào payload
          // For Solana: include blockhashHash and creatorWalletAddress for add_whitelister
          ...(currentChain === 'solana' && blockhashHash && creatorWalletAddress
            ? { blockhashHash, creatorWalletAddress }
            : {}),
        };

        console.log('[useMintLixi] Creating campaign with payload:', {
          groupId: createPayload.groupId,
          startTokenId: createPayload.startTokenId,
          endTokenId: createPayload.endTokenId,
          numTickets: numberOfItem,
        });

        const result = await createCampaign(createPayload).unwrap();
        
        // Compare groupId (handle both string and number)
        const groupIdMatch = String(result.createCampaign.groupId) === String(groupIdNum);
        console.log('[useMintLixi] Campaign created successfully:', {
          campaignId: result.createCampaign._id,
          groupId: result.createCampaign.groupId,
          savedGroupId: groupIdMatch ? '✅ Match' : '❌ Mismatch',
        });
        dispatch(setCreatingStage(CreatingStage.SUCCESS));
        navigate('/lixi');
      } catch (error) {
        dispatch(setCreatingStage(CreatingStage.IDLE));
        console.log(error);

        toast({
          title: 'Error',
          description: 'Oops, có gì đó hoạt động chưa đúng rồi',
          status: 'error',
        });
      }
    },
    [
      fieldState,
      wallet?.address,
      currentChain,
      blockhashHash,
      creatorWalletAddress,
      dispatch,
      upload,
      createCampaign,
      navigate,
      toast,
      isMintLixiFired,
    ],
  );

  // Subscribe to MintLixi events using unified event system
  // Enable for all chains including Solana (Rust now emits MintLixi event)
  useMintLixiEvent(handleMintLixi, {
    contractAddress: lixiBagAddress,
    enabled: !!lixiBagAddress && !!wallet?.address,
  });

  // Handle Solana transactions - query on-chain data when CREATING_ON_SC stage
  useEffect(() => {
    (async () => {
      // Only proceed for Solana when in CREATING_ON_SC stage and not already processed
      if (
        currentChain !== 'solana' ||
        creatingStage !== CreatingStage.CREATING_ON_SC ||
        solanaTxProcessed ||
        isMintLixiFired ||
        !wallet?.address
      ) {
        return;
      }

      console.log('[useMintLixi] Processing Solana transaction (no event, querying on-chain)...');

      try {
        // Validate required data from Redux store
        if (!blockhashHash || !creatorWalletAddress) {
          throw new Error('Missing blockhashHash or creatorWalletAddress from Redux store. Please ensure createCampaign completed successfully.');
        }

        // Use blockhashHash and creatorWalletAddress to derive PDA and fetch group_id from account
        const chainManager = (await import('../ChainManager')).chainManager;
        const { PublicKey } = await import('@solana/web3.js');
        const { solanaPDA } = await import('../solana/pda');
        
        // Derive group state PDA using blockhashHash and creator wallet address
        const creatorPubkey = new PublicKey(creatorWalletAddress);
        const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(creatorPubkey, blockhashHash);
        
        console.log('[useMintLixi] Derived group state PDA:', {
          pda: groupStatePDA.toString(),
          blockhashHash,
          creatorWalletAddress,
        });

        // Get connection from chainManager
        const connection = chainManager.getConnection();
        if (!connection) {
          throw new Error('Failed to get Solana connection');
        }

        // Fetch account info from PDA
        const accountInfo = await connection.getAccountInfo(groupStatePDA);
        if (!accountInfo || !accountInfo.data) {
          throw new Error(`Group state account not found at PDA: ${groupStatePDA.toString()}`);
        }

        // Decode account data using BorshAccountsCoder
        let groupStateAccount: any = null;
        try {
          const { BorshAccountsCoder } = await import('@coral-xyz/anchor');
          const idl = await import('../../idl/lixinft_solana.json');
          if (idl && idl.default) {
            const coder = new BorshAccountsCoder(idl.default as any);
            const decodeAccountNames = ['GroupState', 'groupState'];
            for (const decodeAccountName of decodeAccountNames) {
              try {
                const decoded = coder.decode(decodeAccountName as any, accountInfo.data);
                if (decoded && typeof decoded === 'object') {
                  // Validate group_id - if it's too large (likely reading wrong offset), use manual decode
                  const decodedGroupId = decoded.groupId ?? decoded.group_id;
                  if (decodedGroupId !== undefined && decodedGroupId !== null) {
                    const groupIdValue = typeof decodedGroupId === 'bigint' 
                      ? decodedGroupId 
                      : typeof decodedGroupId === 'string'
                      ? BigInt(decodedGroupId)
                      : BigInt(Number(decodedGroupId));
                    
                    // If group_id is suspiciously large (> 1 million), likely reading wrong offset
                    // Use manual decode instead
                    if (groupIdValue > BigInt(1000000)) {
                      console.warn('[useMintLixi] BorshAccountsCoder decoded suspiciously large group_id:', groupIdValue.toString(), '- using manual decode instead');
                      groupStateAccount = null; // Force manual decode
                      break;
                    }
                  }
                  groupStateAccount = decoded;
                  break;
                }
              } catch (_) {
                continue;
              }
            }
          }
        } catch (decodeError: any) {
          console.warn('[useMintLixi] BorshAccountsCoder decode failed, trying manual decode:', decodeError?.message);
        }

        // If BorshAccountsCoder failed, try manual decode from raw data
        // NOTE: From hex dump analysis, there appears to be 8 bytes of padding after discriminator
        // Actual layout in raw account data:
        // 0-7: discriminator (8 bytes)
        // 8-15: padding (8 bytes) - possibly due to Anchor serialization
        // 16-23: group_id (u64, 8 bytes) - ACTUAL OFFSET
        // 24-55: owner (Pubkey, 32 bytes)
        // 56-59: size (u32, 4 bytes)
        // 60-63: claimed (u32, 4 bytes)
        // 64-79: hash ([u8; 16], 16 bytes)
        // 72-79: start_ticket_id (u64, 8 bytes) - overlaps with hash? Or at 80-87?
        if (!groupStateAccount) {
          const data = accountInfo.data;
          if (data.length >= 88) {
            const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
            // Use actual offsets from hex dump analysis (verified from on-chain data)
            const GROUP_ID_OFFSET = 16; // After discriminator (8) + padding (8)
            const START_TICKET_ID_OFFSET = 72; // From hex dump analysis
            
            const groupIdRaw = dv.getBigUint64(GROUP_ID_OFFSET, true); // little endian
            const startTicketIdRaw = dv.getBigUint64(START_TICKET_ID_OFFSET, true); // little endian
            
            // Keep as BigInt to preserve precision for large numbers
            // Convert to number only if within safe range
            const groupIdNum = Number(groupIdRaw);
            const groupIdValue = groupIdNum > Number.MAX_SAFE_INTEGER || groupIdNum < Number.MIN_SAFE_INTEGER
              ? groupIdRaw.toString() // Keep as string for large numbers
              : groupIdNum; // Use number for safe range
            
            const startTicketIdNum = Number(startTicketIdRaw);
            const startTicketIdValue =
              startTicketIdNum > Number.MAX_SAFE_INTEGER || startTicketIdNum < Number.MIN_SAFE_INTEGER
                ? startTicketIdRaw.toString()
                : startTicketIdNum;

            groupStateAccount = {
              groupId: groupIdValue,
              group_id: groupIdValue,
              startTicketId: startTicketIdValue,
              start_ticket_id: startTicketIdValue,
            };
          } else {
            throw new Error(`Account data too small: ${data.length} bytes (expected at least 56 bytes)`);
          }
        }

        // Extract group_id from decoded account - support both number and BigInt/string
        let groupId: number | string;
        if (groupStateAccount.groupId !== undefined && groupStateAccount.groupId !== null) {
          if (typeof groupStateAccount.groupId === 'number') {
            groupId = groupStateAccount.groupId;
          } else if (typeof groupStateAccount.groupId === 'string') {
            // Already a string (from manual decode for large numbers)
            groupId = groupStateAccount.groupId;
          } else if (typeof groupStateAccount.groupId.toNumber === 'function') {
            // BN or similar object with toNumber method
            const num = groupStateAccount.groupId.toNumber();
            // Check if it's within safe range
            if (num > Number.MAX_SAFE_INTEGER || num < Number.MIN_SAFE_INTEGER) {
              // Convert to string to preserve precision
              groupId = groupStateAccount.groupId.toString();
            } else {
              groupId = num;
            }
          } else {
            // Try to convert to string
            groupId = String(groupStateAccount.groupId);
          }
        } else if (groupStateAccount.group_id !== undefined && groupStateAccount.group_id !== null) {
          if (typeof groupStateAccount.group_id === 'number') {
            groupId = groupStateAccount.group_id;
          } else if (typeof groupStateAccount.group_id === 'string') {
            groupId = groupStateAccount.group_id;
          } else if (typeof groupStateAccount.group_id.toNumber === 'function') {
            const num = groupStateAccount.group_id.toNumber();
            if (num > Number.MAX_SAFE_INTEGER || num < Number.MIN_SAFE_INTEGER) {
              groupId = groupStateAccount.group_id.toString();
            } else {
              groupId = num;
            }
          } else {
            groupId = String(groupStateAccount.group_id);
          }
        } else {
          throw new Error('Could not find group_id in group state account');
        }
        
        // Extract startTicketId from decoded account
        let startTicketIdNum: number;
        if (groupStateAccount.startTicketId !== undefined && groupStateAccount.startTicketId !== null) {
          startTicketIdNum = typeof groupStateAccount.startTicketId === 'number'
            ? groupStateAccount.startTicketId
            : groupStateAccount.startTicketId.toNumber();
        } else if (groupStateAccount.start_ticket_id !== undefined && groupStateAccount.start_ticket_id !== null) {
          startTicketIdNum = typeof groupStateAccount.start_ticket_id === 'number'
            ? groupStateAccount.start_ticket_id
            : groupStateAccount.start_ticket_id.toNumber();
        } else {
          startTicketIdNum = 0;
        }
        const numTickets = fieldState.numberItem;

        console.log('[useMintLixi] Fetched Solana on-chain data:', {
          groupId,
          startTicketId: startTicketIdNum,
          numTickets,
          owner: wallet.address,
          pda: groupStatePDA.toString(),
        });

        // Trigger handleMintLixi with the fetched data
        await handleMintLixi({
          owner: wallet.address,
          groupId: groupId.toString(),
          numTickets,
          startTicketId: startTicketIdNum.toString(),
          purchaseDate: Math.floor(Date.now() / 1000),
        });

        setSolanaTxProcessed(true);
      } catch (error) {
        console.error('[useMintLixi] Error processing Solana transaction:', error);
        dispatch(setCreatingStage(CreatingStage.ERROR));
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to process transaction',
          status: 'error',
        });
      }
    })();
  }, [
    currentChain,
    creatingStage,
    solanaTxProcessed,
    isMintLixiFired,
    wallet?.address,
    fieldState.numberItem,
    blockhashHash,
    creatorWalletAddress,
    handleMintLixi,
    dispatch,
    toast,
  ]);

  return { isMintLixiFired };
}
